import React from 'react';
import { observer } from 'mobx-react-lite';
import { FormattedMessage, useIntl } from 'react-intl';
import Box, { BoxProps } from 'ui-box';
import {
  Card,
  majorScale,
  Tag,
  minorScale,
  Button,
  useAlert,
  Popover,
  Menu,
  MenuItem,
  MenuDivider,
  useToast,
} from '../../npaper';
import { ITeamMember } from '../../stores/TeamMemberStore';
import { UserThumbnail } from './UserThumbnail';
import { useStores } from '../../stores';

export const UserCard = observer(
  ({
    member,
    ...props
  }: {
    member: ITeamMember;
  } & BoxProps<any>) => {
    const intl = useIntl();
    const toaster = useToast();
    const { teamMemberStore } = useStores();
    const removeUserAlert = useAlert({
      message: intl.formatMessage({
        defaultMessage: 'Are you sure you want to remove this user?',
        description: 'Remove user alert title',
      }),
      intent: 'danger',
      icon: 'warning-sign',
      confirmButtonText: intl.formatMessage({
        id: 'defaults.remove',
        defaultMessage: 'Remove',
      }),
    });

    const removeUser = () =>
      removeUserAlert.show(async () => {
        // TODO: Handle user removal
        try {
          await teamMemberStore.deleteItem(member);
          toaster.addToast({
            intent: 'success',
            message: intl.formatMessage({
              defaultMessage: 'User has been removed from account',
            }),
          });
        } catch (err) {
          if (err.response && err.response.status === 400) {
            toaster.addToast({
              message: intl.formatMessage({
                defaultMessage: 'You cannot remove the owner',
              }),
              intent: 'warning',
            });
          } else {
            toaster.showDefaultError(err);
          }
        }
      });

    return (
      <Card
        padding={majorScale(1)}
        display="flex"
        alignItems="center"
        {...props}
      >
        <UserThumbnail
          size={40}
          user={member.user}
          marginRight={majorScale(1)}
        />
        <Box>
          <p>{member.user.name}</p>
          <Box className="p">
            <Box is="span" marginRight={majorScale(1)} className="muted">
              {member.user.email}
            </Box>
            {member.is_owner && (
              <Tag marginRight={minorScale(1)} small intent="warning">
                <FormattedMessage defaultMessage="Owner" />
              </Tag>
            )}
            {member.is_admin && (
              <Tag marginRight={minorScale(1)} small>
                <FormattedMessage
                  defaultMessage="Admin"
                  description="Admin role tag"
                />
              </Tag>
            )}
            {member.is_salesman && (
              <Tag marginRight={minorScale(1)} small>
                <FormattedMessage
                  defaultMessage="Sales"
                  description="Sales role tag"
                />
              </Tag>
            )}
            {member.is_purchaser && (
              <Tag marginRight={minorScale(1)} small>
                <FormattedMessage
                  defaultMessage="Purchasing"
                  description="Purchasing role tag"
                />
              </Tag>
            )}
            {member.is_warehouse_employee && (
              <Tag marginRight={minorScale(1)} small>
                <FormattedMessage
                  defaultMessage="Warehousing"
                  description="Warehouse role tag"
                />
              </Tag>
            )}
          </Box>
        </Box>

        <Box marginLeft="auto">
          <Popover
            content={(close) => (
              <Menu>
                <MenuDivider text="Permissions" />
                <MenuItem
                  text={intl.formatMessage({ defaultMessage: 'Admin' })}
                  icon={member.is_admin ? 'tick-circle' : 'circle'}
                  onClick={() => {
                    teamMemberStore
                      .updateItem(member, {
                        is_admin: !member.is_admin,
                      })
                      .catch(toaster.showDefaultError);
                  }}
                />
                <MenuItem
                  text={intl.formatMessage({ defaultMessage: 'Sales' })}
                  icon={member.is_salesman ? 'tick' : 'circle'}
                  onClick={() => {
                    teamMemberStore
                      .updateItem(member, {
                        is_salesman: !member.is_salesman,
                      })
                      .catch(toaster.showDefaultError);
                  }}
                />
                <MenuItem
                  text={intl.formatMessage({ defaultMessage: 'Purchasing' })}
                  icon={member.is_purchaser ? 'tick' : 'circle'}
                  onClick={() => {
                    teamMemberStore
                      .updateItem(member, {
                        is_purchaser: !member.is_purchaser,
                      })
                      .catch(toaster.showDefaultError);
                  }}
                />
                <MenuItem
                  text={intl.formatMessage({ defaultMessage: 'Warehousing' })}
                  icon={member.is_warehouse_employee ? 'tick' : 'circle'}
                  onClick={() => {
                    teamMemberStore
                      .updateItem(member, {
                        is_warehouse_employee: !member.is_warehouse_employee,
                      })
                      .catch(toaster.showDefaultError);
                  }}
                />
                <MenuDivider />
                <MenuItem
                  text={intl.formatMessage({
                    defaultMessage: 'Remove user',
                  })}
                  icon="trash"
                  intent="danger"
                  onClick={(e) => {
                    close();
                    e.stopPropagation();
                    removeUser();
                  }}
                />
              </Menu>
            )}
          >
            <Button minimal iconLeft="more" />
          </Popover>
        </Box>
      </Card>
    );
  }
);
